import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './Report.css';

export default function Report() {
  const { companyId, orgCode } = useParams();
  const paramCompany = companyId || orgCode;
  console.log("company param from URL:", paramCompany);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [trainerName, setTrainerName] = useState('');
  const [companyProgramName, setCompanyProgramName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modulesReport, setModulesReport] = useState([]);
  const [selectedModuleReportCourse, setSelectedModuleReportCourse] = useState('all');
  const [moduleNameMap, setModuleNameMap] = useState({});

  // Fetch companies on mount
  useEffect(() => {
    let localCompanyCode = paramCompany || localStorage.getItem("orgCode") || localStorage.getItem("companyId");

    // Clean up invalid or stringified null/undefined values
    if (!localCompanyCode || localCompanyCode === 'undefined' || localCompanyCode === 'null' || String(localCompanyCode).trim() === '') {
      localCompanyCode = null;
    }

    if (localCompanyCode) {
      setSelectedCompany(localCompanyCode);
    } else {
      console.log("Failed to Load Company — orgCode not found in localStorage");
      setError("Organisation not found. Please log in again.");
      setLoading(false);
    }
  }, [paramCompany]);

  // Fetch all data when company changes
  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyData(selectedCompany);
    }
  }, [selectedCompany]);

  const fetchCompanyData = async (companyId) => {
    try {
      setLoading(true);

      let resolvedCompanyId = companyId;

      // Try to find a matching company ID if it doesn't exist directly
      try {
        const docRef = doc(db, `CorporateClients/${resolvedCompanyId}`);
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          const clientsRef = collection(db, "CorporateClients");
          const clientsSnap = await getDocs(clientsRef);
          let candidates = [];
          clientsSnap.forEach((clientDoc) => {
            if (clientDoc.id.toLowerCase().includes(companyId.toLowerCase())) {
              candidates.push(clientDoc.id);
            }
          });
          if (candidates.length > 0) {
            if (candidates.length === 1) {
              resolvedCompanyId = candidates[0];
            } else {
              const loggedInStudentId = localStorage.getItem("studentId") || "Abhi_001";
              let found = false;
              for (const candidate of candidates) {
                const studentRef = doc(db, `CorporateClients/${candidate}/studentInfo/${loggedInStudentId}`);
                const studentSnap = await getDoc(studentRef);
                if (studentSnap.exists()) {
                  resolvedCompanyId = candidate;
                  found = true;
                  break;
                }
              }
              if (!found) {
                resolvedCompanyId = candidates[0];
              }
            }
          } else {
            resolvedCompanyId = "GSK2025A";
          }
        }
      } catch (err) {
        console.error("Error resolving companyId:", err);
      }

      // Try to read the company document to get a friendly companyName
      try {
        const companyRef = doc(db, `CorporateClients/${resolvedCompanyId}`);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          const data = companySnap.data();
          setCompanyName(data?.companyName || resolvedCompanyId);
          setTrainerName(data?.trainerName || '');
          setCompanyProgramName(data?.programName || data?.programs || '');
        } else {
          setCompanyName(resolvedCompanyId);
          setTrainerName('');
          setCompanyProgramName('');
        }
      } catch (e) {
        // fallback to resolvedCompanyId if any error
        setCompanyName(resolvedCompanyId);
        setTrainerName('');
        setCompanyProgramName('');
      }

      setError(null);

      // Fetch program info for modules report
      await fetchModulesReport(resolvedCompanyId);
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isCapstoneCourse = (courseName) => /capstone/i.test(String(courseName || ''));

  const fetchModulesReport = async (companyId) => {
    try {
      const programInfoRef = collection(db, `CorporateClients/${companyId}/programInfo`);
      const programInfoSnapshot = await getDocs(programInfoRef);

      const modulesData = [];
      const moduleMap = {};

      // Fetch modules for each course
      const coursePromises = [];
      programInfoSnapshot.forEach((courseDoc) => {
        const courseName = courseDoc.id;

        const modulesRef = collection(db, `CorporateClients/${companyId}/programInfo/${courseName}/modules`);
        coursePromises.push(
          getDocs(modulesRef).then((modulesSnapshot) => {
            modulesSnapshot.forEach((moduleDoc) => {
              const moduleData = moduleDoc.data();
              const displayName =
                moduleData.name ||
                moduleData.moduleName ||
                moduleDoc.id;

              moduleMap[`${courseName}|${moduleDoc.id}`] =
                displayName;

              modulesData.push({
                id: moduleDoc.id,
                course: courseName,
                moduleName: displayName,
                startDate: moduleData.startDate || '-',
                endDate: moduleData.endDate || '-',
                status: moduleData.status || '-'
              });
            });
          })
        );
      });

      // Wait for all module fetches to complete
      await Promise.all(coursePromises);

      const courseIds = Array.from(new Set(modulesData.map(m => m.course))).sort();
      setModulesReport(modulesData);
      setModuleNameMap(moduleMap);

      // Set default selected course for modules report
      if (courseIds.length > 0) {
        setSelectedModuleReportCourse('all');
      }
    } catch (err) {
      console.error('Error fetching modules report:', err);
    }
  };

  const renderModulesReport = () => {
    if (modulesReport.length === 0) {
      return <div className="no-data">No modules data available</div>;
    }

    const grouped = modulesReport.reduce((acc, module) => {
      const course = module.course || 'Unknown';
      if (isCapstoneCourse(course)) return acc;
      if (!acc[course]) acc[course] = [];
      acc[course].push(module);
      return acc;
    }, {});

    const allCourses = Object.keys(grouped).sort();

    const displayedCourses = selectedModuleReportCourse === 'all' || !selectedModuleReportCourse
      ? allCourses
      : allCourses.filter(c => c === selectedModuleReportCourse);

    return (
      <div className="report-container">
        <div className="course-selector-wrapper">
          <label htmlFor="modules-report-course-select">Select Course:</label>
          <select
            id="modules-report-course-select"
            value={selectedModuleReportCourse || 'all'}
            onChange={(e) => setSelectedModuleReportCourse(e.target.value)}
            className="select-dropdown"
          >
            <option value="all">All</option>
            {allCourses.map((course) => (
              <option key={course} value={course}>
                {course}
              </option>
            ))}
          </select>
        </div>

        {displayedCourses.length === 0 ? (
          <div className="no-data">No modules available for selected course (excluding capstone)</div>
        ) : (
          <div className="assessment-all-courses">
            {displayedCourses.map((course) => (
              <div key={`group-${course}`} className="assessment-course-section">
                <div className="assessment-course-title">{course}</div>
                <div className="excel-table-wrapper">
                  <table className="excel-table modules-report-table">
                    <thead>
                      <tr className="course-header-row">
                        <th className="module-name-col">Module Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[course].map((module) => (
                        <tr key={`${course}|${module.id}`}>
                          <td className="module-name-col">{module.moduleName}</td>
                          <td>{module.startDate}</td>
                          <td>{module.endDate}</td>
                          <td>
                            <span className={`status-badge ${String(module.status || '').toLowerCase()}`}>
                              {module.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container">

      {error && <div className="error">Error: {error}</div>}

      {!loading && (
        <div className="tab-content">
          {renderModulesReport()}
        </div>
      )}

      {loading && <div className="loading">Loading data...</div>}
    </div>
  );
}
